import { useMutation, useQuery } from "@tanstack/react-query";
import { api } from "api";
import { T_Omit, T_Populate } from "types/_utils.types";
import { OrganisationSchemaDto } from "dtos/organisation.dto";
import { RoleTypes, UserSchemaDto } from "dtos/user.dto";
import { useNavigate } from "react-router-dom";
import { useAppDispatch } from "store";
import { changeUserPhone, setUser } from "store/userSlice";
import { storedTokens } from "utils/storedTokens";
import { T_Api_Paginated, T_Api_Success_Res } from "./api.types";
import { MENU_ITEMS } from "layout/DashboardLayout/DashboardLayout.utils";
import { queryClient } from "utils/queryClient";
import { message } from "antd";
import { useDebounce } from "hooks/use-debounce";

type T_Statistics = {
  totalConvertedLeads: number;
  totalLeads: number;
  lastMonthCommission: number;
  thisMonthCommission: number;
  totalApplications: number;
  totalApplicationsConverted: number;
  nextMonthCommission: number;
  MTDCommission: number;
};
type T_AffiliateHeirarchyUser = UserSchemaDto & T_Statistics;
export type T_NestedChildrenUser = T_AffiliateHeirarchyUser & { children: T_AffiliateHeirarchyUser[] };
export type T_AffiliateApplication = {
  _id: string;
  app_key: string;
  client: string;
  status: string;
  total_debt: number;
  has_lawsuits: boolean;
  strategy: string;
  initial_payment: number;
  weekly_payment: number;
  last_step: string;
  onboarded: boolean;
  initial_commission: number;
  weekly_commission: number;
};
export const userService = {
  useLogin: () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    return useMutation({
      mutationKey: ["login"],
      mutationFn: async (body: { email?: string; password?: string }) => {
        const res = await api.post("/auth/login", body);
        return res.data as T_Api_Success_Res<{
          token: string;
          refreshToken: string;
          data: Pick<
            UserSchemaDto,
            "_id" | "slug" | "name" | "email" | "role" | "last_login" | "is_verified" | "avatar_url" | "phone"
          >;
        }>;
      },
      onSuccess: (data) => {
        // queryClient.prefetchQuery({ queryKey: ["user", "session"] });
        const user = data.result.data;
        dispatch(
          setUser({
            _id: user._id,
            slug: user.slug,
            email: user.email,
            name: user.name,
            role: user.role,
            avatar_url: user.avatar_url,
            is_verified: user.is_verified,
            last_login: user.last_login,
            phone: user.phone,
          }),
        );
        storedTokens.accessToken.setToken(data.result.token);
        storedTokens.refreshToken.setToken(data.result.refreshToken);

        const userRole = user.role;
        const basePage = MENU_ITEMS[userRole]?.[0]?.path;
        if (basePage) {
          navigate(basePage);
        }
        // if ([RoleTypes.CASE_ADMIN, RoleTypes.CASE_MANAGER].includes(userRole)) {
        //   navigate(`/dashboard/lead-cases`);
        // } else if ([RoleTypes.CONTENT_ADMIN, RoleTypes.CONTENT_MANAGER].includes(userRole)) {
        //   navigate(`/dashboard/blogs`);
        // } else {
        //   navigate("/dashboard");
        // }
      },
    });
  },
  useLogout: () => {
    const navigate = useNavigate();
    return useMutation({
      mutationFn: async () => {
        const { data } = await api.post("/auth/logout");
        return data;
      },
      onSuccess() {
        storedTokens.accessToken.removeToken();
        navigate("/");
        sessionStorage.removeItem("recentUsers");
      },
    });
  },

  useLoginAsOther: () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    return useMutation({
      mutationKey: ["login/other"],
      mutationFn: async (body: { email: string }) => {
        const res = await api.post("/auth/login-other", body);
        return res.data as T_Api_Success_Res<{
          token: string;
          refreshToken: string;
          data: Pick<
            UserSchemaDto,
            "_id" | "slug" | "name" | "email" | "role" | "last_login" | "is_verified" | "avatar_url" | "phone"
          >;
        }>;
      },
      onSuccess: (data) => {
        // queryClient.prefetchQuery({ queryKey: ["user", "session"] });
        const user = data.result.data;
        dispatch(
          setUser({
            _id: user._id,
            slug: user.slug,
            email: user.email,
            name: user.name,
            role: user.role,
            avatar_url: user.avatar_url,
            is_verified: user.is_verified,
            last_login: user.last_login,
            phone: user.phone,
          }),
        );

        //backup of original account token
        if (!storedTokens.adminAccessToken.getToken() && !storedTokens.adminRefreshToken.getToken()) {
          storedTokens.adminAccessToken.setToken(storedTokens.accessToken.getToken()!);
          storedTokens.adminRefreshToken.setToken(storedTokens.refreshToken.getToken()!);
        }

        storedTokens.accessToken.setToken(data.result.token);
        storedTokens.refreshToken.setToken(data.result.refreshToken);

        const recentUsersKey = "recentUsers";
        try {
          const existing = JSON.parse(sessionStorage.getItem(recentUsersKey) || "[]");

          const filtered = existing.filter((u: any) => u.email !== user.email);

          filtered.unshift({
            name: user.name,
            email: user.email,
            last_login: user.last_login,
          });

          const trimmed = filtered.slice(0, 3);

          sessionStorage.setItem(recentUsersKey, JSON.stringify(trimmed));
        } catch (err) {
          console.error("Failed to update recent users:", err);
        }

        navigate("/dashboard");
        queryClient.clear();
      },
    });
  },

  usePhoneLoginVerify: () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    return useMutation({
      mutationKey: ["login/phone"],
      mutationFn: async (body: { phone: string; code: string; otp: string }) => {
        const res = await api.post(`/user/verify-phone/${body.otp}`, { phone: body.phone, code: body.code });
        return res.data as T_Api_Success_Res<{
          token: string;
          refreshToken: string;
          data: Pick<
            UserSchemaDto,
            "_id" | "slug" | "name" | "email" | "role" | "last_login" | "is_verified" | "avatar_url" | "phone"
          >;
        }>;
      },
      onSuccess: (data) => {
        // queryClient.prefetchQuery({ queryKey: ["user", "session"] });
        const user = data.result.data;
        dispatch(
          setUser({
            _id: user._id,
            slug: user.slug,
            email: user.email,
            name: user.name,
            role: user.role,
            avatar_url: user.avatar_url,
            is_verified: user.is_verified,
            last_login: user.last_login,
            phone: user.phone,
          }),
        );
        storedTokens.accessToken.setToken(data.result.token);
        storedTokens.refreshToken.setToken(data.result.refreshToken);
        const userRole = user.role;
        const basePage = MENU_ITEMS[userRole]?.[0]?.path;
        if (basePage) {
          navigate(basePage);
        }
        // if ([RoleTypes.CASE_ADMIN, RoleTypes.CASE_MANAGER].includes(userRole)) {
        //   navigate(`/dashboard/lead-cases`);
        // } else if ([RoleTypes.CONTENT_ADMIN, RoleTypes.CONTENT_MANAGER].includes(userRole)) {
        //   navigate(`/dashboard/blogs`);
        // } else {
        //   navigate("/dashboard");
        // }
      },
    });
  },

  usePhoneLoginVerifyAuth: () => {
    const dispatch = useAppDispatch();

    return useMutation({
      mutationKey: ["verify/phone/auth"],
      mutationFn: async (body: { phone: string; code: string; otp: string }) => {
        const res = await api.post(`/user/verify/phone/${body.otp}`, { phone: body.phone, code: body.code });
        return res.data as T_Api_Success_Res<{
          data: Pick<UserSchemaDto, "phone">;
        }>;
      },
      onSuccess: (data) => {
        // queryClient.prefetchQuery({ queryKey: ["user", "session"] });
        const user = data.result.data;
        dispatch(changeUserPhone(user.phone));

        message.success("Phone number verified!");
      },
    });
  },

  // useRegister: () => {
  //   return useMutation({
  //     mutationKey: ["register"],
  //     mutationFn: async (body: { email: string; password: string }) => {
  //       return await api.post(`/user/register`, body);
  //     },
  //   });
  // },

  useAddUser: () => {
    return useMutation({
      mutationKey: ["user", "add"],
      mutationFn: async (body: {
        name: string;
        email: string;
        password: string;
        role: RoleTypes;
        assigned_to?: string;
      }) => {
        const res = await api.post(`/user/add`, body);
        return res.data.result;
      },
    });
  },

  usePhoneLogin: () => {
    return useMutation({
      mutationKey: ["/login/phone"],
      mutationFn: async (body: { phone?: string; code?: string; bypass?: boolean }) => {
        const res = await api.post("/user/login/phone", body);
        return res.data;
      },
    });
  },

  useResendOTP: () => {
    return useMutation({
      mutationKey: ["/resend-otp"],
      mutationFn: async (body: { phone?: string; code?: string }) => {
        const res = await api.post("/user/resend-otp", body);
        return res.data;
      },
    });
  },

  useUpdateUser: () => {
    return useMutation({
      mutationKey: ["user", "update"],
      mutationFn: async (body: {
        userid: string;
        name: string;
        email: string;
        role: RoleTypes;
        assigned_to?: string;
        affiliate_code: string;
        phone?: UserSchemaDto["phone"];
      }) => {
        console.log("Updating user with data:", body);
        const res = await api.patch(`/user/update/${body.userid}`, body);
        return res.data;
      },
    });
  },

  useDeleteUser: () => {
    return useMutation({
      mutationKey: ["user", "delete"],
      mutationFn: async (body: { userid: string }) => {
        return await api.post(`/user/remove`, body);
      },
    });
  },

  useValidateAffiliateCode: () => {
    return useMutation({
      mutationKey: ["user", "affiliate-code"],
      mutationFn: async (body: { affiliate_code: string }) => {
        const res = await api.post(`/user/validate`, body);
        return res.data.result.isFound;
      },
    });
  },

  useBanorUnbanUser: () => {
    return useMutation({
      mutationKey: ["user", "ban-unban"],
      mutationFn: async (body: { userid: string; is_banned: boolean }) => {
        const res = await api.patch(`/user/banOrunban`, body);
        return res.data;
      },
    });
  },

  useUpdatePasswordUser: () => {
    return useMutation({
      mutationKey: ["user", "updatepassword"],
      mutationFn: async (body: { id: string; newpassword: string }) => {
        const res = await api.patch(`/user/password/${body.id}`, { password: body.newpassword });
        return res.data;
      },
    });
  },

  useResetOTPAttemptsUser: () => {
    return useMutation({
      mutationKey: ["user", "reset-attempt"],
      mutationFn: async (body: { id: string }) => {
        const res = await api.patch(`/user/reset-attempt/${body.id}`);
        return res.data;
      },
    });
  },

  useGetPaginated: ({
    page,
    limit,
    role,
    search,
    startDate,
    endDate,
    sort,
    ban_status,
  }: {
    page: number;
    limit: number;
    role: string[];
    search?: string;
    startDate?: string;
    endDate?: string;
    sort?: string;
    ban_status?: string;
  }) => {
    const debouncedSearch = useDebounce(search, 500);

    return useQuery({
      queryKey: ["user", "paginated", page, limit, role, debouncedSearch, startDate, endDate, sort, ban_status],
      queryFn: async () => {
        const queryParams = new URLSearchParams({
          page: page.toString(),
          limit: limit.toString(),
          userType: JSON.stringify(role),
        });
        if (debouncedSearch) queryParams.append("search", debouncedSearch);
        if (startDate) queryParams.append("startDate", startDate);
        if (endDate) queryParams.append("endDate", endDate);
        if (sort) queryParams.append("sort", sort);
        if (ban_status) queryParams.append("ban_status", ban_status);

        const res = await api.get(`/user/all?${queryParams.toString()}`);
        return res.data as T_Api_Success_Res<T_Api_Paginated<UserSchemaDto>>;
      },
    });
  },

  useGetUserHeirarchy: () => {
    return useQuery({
      queryKey: ["user", "heirarchy"],
      queryFn: async () => {
        const res = await api.get(`/user/heirarchy`);
        const data = res.data as T_Api_Success_Res<{ data: UserSchemaDto & { children?: UserSchemaDto[] } }>;

        return data.result?.data?.children;
      },
    });
  },
  useGetAffiliateHeirarchy: () => {
    return useQuery({
      queryKey: ["user", "affiliate/heirarchy"],
      queryFn: async () => {
        const res = await api.get(`/user/affiliate/heirarchy`);
        const data = res.data as T_Api_Success_Res<{
          data: T_NestedChildrenUser;
          statistics: T_Statistics;
        }>;

        return data.result;
      },
    });
  },
  useGetOneAffiliateStatistics: (affiliateId: string | undefined) => {
    return useQuery({
      queryKey: ["user", "affiliate/statistics", affiliateId],
      queryFn: async () => {
        const res = await api.get(`/user/affiliate/statistics/${affiliateId}`);
        const data = res.data as T_Api_Success_Res<{ statistics: T_Statistics }>;
        return data.result;
      },
      enabled: !!affiliateId,
    });
  },
  useGetOneAffiliateApllications: (affiliateId: string | undefined, params: { page: number; limit: number }) => {
    return useQuery({
      queryKey: ["user", "affiliate/item", affiliateId, params],
      queryFn: async () => {
        const res = await api.get(`/application/lead-application/details/${affiliateId}`, { params });
        const data = res.data as T_Api_Success_Res<T_Api_Paginated<T_AffiliateApplication>>;
        return data.result;
      },
      enabled: !!affiliateId,
    });
  },

  useGetCurrentUser: () => {
    const dispatch = useAppDispatch();
    return useQuery({
      queryKey: ["user", "me"],
      queryFn: async () => {
        const res = await api.get(`/user/me`);
        const user = res.data?.result as T_Populate<
          T_Omit<UserSchemaDto, "password" | "liked_posts" | "liked_comments" | "saved_blogs">,
          {
            organisation: OrganisationSchemaDto;
            assigned_to: UserSchemaDto;
          }
        >;
        dispatch(
          setUser({
            _id: user._id,
            slug: user.slug,
            email: user.email,
            name: user.name,
            role: user.role,
            avatar_url: user.avatar_url,
            is_verified: user.is_verified,
            last_login: user.last_login,
            phone: user.phone,
          }),
        );
        return user;
      },
    });
  },
  useRegeneratePortalAccessKey: () => {
    return useMutation({
      mutationKey: ["user", "reset-portal-access-key"],
      mutationFn: async (id: string) => {
        const res = await api.post(`/user/regenerate/portal-access-key/${id}`);
        return res.data.result;
      },
    });
  },
};
